import { useMemo, useState } from "react";

import { Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  GLOBAL_WILDCARD,
  PERMISSION_MODULES,
  type PermissionOrWildcard,
} from "@/features/rbac/permissions/catalog";

interface PermissionPickerProps {
  /** Permisos y comodines seleccionados. */
  value: readonly PermissionOrWildcard[];
  onChange: (next: PermissionOrWildcard[]) => void;
  /** Deshabilita toda edición (rol con permiso total inmutable, por ejemplo). */
  disabled?: boolean;
}

function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Selector de permisos agrupado por módulo, renderizado directamente desde el
 * catálogo. Cuando se añade una funcionalidad nueva al catálogo, aparece aquí
 * sin tocar este componente.
 *
 * La casilla "todo el módulo" guarda el comodín `module:*` en lugar de la lista
 * expandida, así el rol hereda automáticamente los permisos que se añadan
 * después a ese módulo.
 */
export function PermissionPicker({
  value,
  onChange,
  disabled = false,
}: PermissionPickerProps) {
  const [search, setSearch] = useState("");
  const selected = useMemo(() => new Set<string>(value), [value]);
  const hasGlobal = selected.has(GLOBAL_WILDCARD);

  const query = normalize(search.trim());

  const modules = useMemo(() => {
    if (!query) return PERMISSION_MODULES;

    return PERMISSION_MODULES.map((module) => ({
      ...module,
      permissions: module.permissions.filter(
        (permission) =>
          normalize(permission.label).includes(query) ||
          normalize(permission.description).includes(query) ||
          permission.id.includes(query)
      ),
    })).filter(
      (module) => module.permissions.length > 0 || normalize(module.label).includes(query)
    );
  }, [query]);

  const emit = (next: Set<string>) => {
    onChange([...next].sort() as PermissionOrWildcard[]);
  };

  const togglePermission = (permissionId: string, moduleId: string) => {
    const next = new Set(selected);

    // Desmarcar dentro de un módulo con comodín exige expandirlo primero:
    // si no, quitar una casilla no tendría ningún efecto visible.
    if (next.has(`${moduleId}:*`)) {
      next.delete(`${moduleId}:*`);
      const module = PERMISSION_MODULES.find((candidate) => candidate.id === moduleId);
      for (const permission of module?.permissions ?? []) next.add(permission.id);
    }

    if (next.has(permissionId)) next.delete(permissionId);
    else next.add(permissionId);

    emit(next);
  };

  const toggleModule = (moduleId: string) => {
    const next = new Set(selected);
    const module = PERMISSION_MODULES.find((candidate) => candidate.id === moduleId);
    const permissionIds = (module?.permissions ?? []).map((permission) => permission.id);

    if (next.has(`${moduleId}:*`)) {
      next.delete(`${moduleId}:*`);
      for (const id of permissionIds) next.delete(id);
    } else {
      for (const id of permissionIds) next.delete(id);
      next.add(`${moduleId}:*`);
    }

    emit(next);
  };

  const isPermissionChecked = (permissionId: string, moduleId: string): boolean =>
    hasGlobal || selected.has(`${moduleId}:*`) || selected.has(permissionId);

  const moduleState = (moduleId: string, permissionIds: readonly string[]) => {
    if (hasGlobal || selected.has(`${moduleId}:*`)) return "all" as const;
    const count = permissionIds.filter((id) => selected.has(id)).length;
    if (count === 0) return "none" as const;
    return count === permissionIds.length ? "all" : ("some" as const);
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar permiso o módulo…"
          className="pl-9"
        />
      </div>

      {hasGlobal && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          Este rol tiene <strong>acceso total</strong>. Concede cualquier permiso
          existente y también los que se añadan en el futuro.
        </p>
      )}

      <div className="max-h-[45vh] space-y-4 overflow-y-auto pr-1">
        {modules.map((module) => {
          const permissionIds = module.permissions.map((permission) => permission.id);
          const state = moduleState(module.id, permissionIds);

          return (
            <div key={module.id} className="rounded-lg border p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{module.label}</p>
                    {state === "all" && <Badge variant="secondary">Todo</Badge>}
                    {state === "some" && <Badge variant="outline">Parcial</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground">{module.description}</p>
                </div>

                <label className="flex shrink-0 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={state === "all"}
                    ref={(element) => {
                      if (element) element.indeterminate = state === "some";
                    }}
                    disabled={disabled || hasGlobal}
                    onChange={() => toggleModule(module.id)}
                  />
                  Todo el módulo
                </label>
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {module.permissions.map((permission) => (
                  <label
                    key={permission.id}
                    className="flex items-start gap-2 rounded-md p-2 text-sm hover:bg-muted/50"
                    title={permission.description}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4"
                      checked={isPermissionChecked(permission.id, module.id)}
                      disabled={disabled || hasGlobal}
                      onChange={() => togglePermission(permission.id, module.id)}
                    />
                    <span>
                      <span className="block">{permission.label}</span>
                      <code className="text-xs text-muted-foreground">
                        {permission.id}
                      </code>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}

        {modules.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Ningún permiso coincide con «{search}».
          </p>
        )}
      </div>
    </div>
  );
}
